import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

import Rules from './rules/Rules';
import Totals from './totals/Totals';

function TransferMarket() {
  const [activeTab, setActiveTab] = useState('rules'); // 'rules' | 'totals'
  const [recalculating, setRecalculating] = useState(false);
  const { apiBaseUrl } = useAuth();

  const handleRecalculateAll = async () => {
    if (!window.confirm("آیا از محاسبه مجدد همه امتیازات اطمینان دارید؟")) {
      return;
    }

    try {
      setRecalculating(true);
      const response = await axios.post(
        `${apiBaseUrl}/api/transfer-market/admin/recalculate-all`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      
      console.log("Recalculate Response:", response);
      alert("محاسبه مجدد با موفقیت انجام شد!");
    } catch (error) {
      console.error("❌ خطا در محاسبه مجدد:", error);
      alert("خطا در محاسبه مجدد امتیازات");
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">بازار نقل و انتقالات</h1>
          <p className="text-gray-400">مدیریت قوانین و مجموع امتیازات</p>
        </motion.div>

        {/* Tab Switch + Recalculate Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center justify-between gap-4 mb-6"
        >
          <div className="bg-gray-800 rounded-xl p-2 inline-flex gap-2">
            <button
              onClick={() => setActiveTab('rules')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'rules'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>قوانین</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('totals')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'totals'
                  ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg shadow-green-500/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>مجموع امتیازات</span>
              </div>
            </button>
          </div>

          {/* Recalculate Button */}
          <button
            onClick={handleRecalculateAll}
            disabled={recalculating}
            className="rounded-xl px-6 py-3 font-bold transition-all duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
            style={{
              background: recalculating 
                ? "linear-gradient(135deg, rgba(168, 85, 247, 0.5) 0%, rgba(124, 58, 237, 0.5) 100%)"
                : "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
              boxShadow: "0 4px 20px rgba(168, 85, 247, 0.4)",
              color: "white",
            }}
          >
            {recalculating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-3 border-white border-t-transparent"></div>
                <span>در حال محاسبه...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>محاسبه مجدد همه</span>
              </>
            )}
          </button>
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'rules' ? (
            <motion.div
              key="rules"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-gray-800 rounded-xl p-6 border border-gray-700"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">مدیریت قوانین</h2>
                  <p className="text-sm text-gray-400">ایجاد، ویرایش و حذف قوانین بازار</p>
                </div>
              </div>
              
              {/* Rules Content - CRUD */}
              <Rules></Rules>
            </motion.div>
          ) : (
            <motion.div
              key="totals"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-gray-800 rounded-xl p-6 border border-gray-700"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-green-600 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">مجموع امتیازات</h2>
                  <p className="text-sm text-gray-400">مشاهده مجموع امتیازات کاربران (فقط خواندنی)</p>
                </div>
              </div>
              
              {/* Totals Content - GET only */}
             <Totals></Totals>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default TransferMarket;